import React, { useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { BoardSearchFilterPanelRouter } from './BoardSearchFilterPanelRouter';
import { BoardSearchTechnicianOsFuturaPanel } from './BoardSearchTechnicianPopoverPanels';
import { getFilterWidthHint, measurePopoverWidth } from './popoverWidthUtils';
import { isSimplePickerFilterType } from './simplePickerFilterTypes';

const POPOVER_MAX_HEIGHT = 380;
const CIDADE_POPOVER_MAX_HEIGHT = 460;
const VIEWPORT_PAD = 8;

function computePopoverMaxHeight(anchorRect, flipped) {
  const available = flipped
    ? anchorRect.top - VIEWPORT_PAD - 4
    : window.innerHeight - anchorRect.bottom - VIEWPORT_PAD - 4;
  const cap = Math.min(CIDADE_POPOVER_MAX_HEIGHT, window.innerHeight - VIEWPORT_PAD * 2);
  return Math.max(200, Math.min(cap, available));
}

function computePopoverPosition(anchorRect, width, filterType) {
  const w = width;
  let left = anchorRect.left;
  let top = anchorRect.bottom + 4;

  if (left + w > window.innerWidth - VIEWPORT_PAD) {
    left = window.innerWidth - w - VIEWPORT_PAD;
  }
  if (left < VIEWPORT_PAD) left = VIEWPORT_PAD;

  const spaceBelow = window.innerHeight - anchorRect.bottom - VIEWPORT_PAD;
  let flipped = false;
  if (spaceBelow < 220 && anchorRect.top > 220) {
    top = anchorRect.top - 4;
    flipped = true;
  }

  const layout = {
    position: 'fixed',
    top,
    left,
    width: w,
    boxSizing: 'border-box',
    zIndex: 1110,
    flipped,
  };

  if (filterType && filterType !== 'osFutura') {
    const maxHeight = computePopoverMaxHeight(anchorRect, flipped);
    layout.maxHeight = maxHeight;
    layout['--bsf-cidade-popover-max-h'] = `${maxHeight}px`;
  }

  return layout;
}

export function BoardSearchFilterPopover({
  anchorRect,
  filterType,
  filterScope,
  onClose,
  onApply,
  displayFilters,
  countsProps,
  previsaoWithCounts,
  futuraOrders,
  retornoCounts,
  panelRemountKey,
}) {
  const popoverRef = useRef(null);
  const lockedWidthRef = useRef(null);
  const measuredFilterRef = useRef(null);
  const [layout, setLayout] = useState(null);

  const anchorKey = anchorRect
    ? `${anchorRect.top}|${anchorRect.left}|${anchorRect.bottom}|${anchorRect.right}`
    : null;

  useLayoutEffect(() => {
    if (!anchorRect || !filterType || filterType === 'osFutura') {
      setLayout(null);
      lockedWidthRef.current = null;
      measuredFilterRef.current = null;
      return undefined;
    }

    const filterChanged = measuredFilterRef.current !== filterType;
    if (filterChanged) {
      measuredFilterRef.current = filterType;
      lockedWidthRef.current = getFilterWidthHint(filterType);
    }

    const width = lockedWidthRef.current || getFilterWidthHint(filterType);
    const nextLayout = computePopoverPosition(anchorRect, width, filterType);
    setLayout((prev) => {
      if (
        prev &&
        prev.top === nextLayout.top &&
        prev.left === nextLayout.left &&
        prev.width === nextLayout.width &&
        prev.flipped === nextLayout.flipped &&
        prev.maxHeight === nextLayout.maxHeight
      ) {
        return prev;
      }
      return nextLayout;
    });

    if (filterChanged) {
      const raf = requestAnimationFrame(() => {
        const measured = measurePopoverWidth(popoverRef.current, filterType);
        lockedWidthRef.current = measured;
        const measuredLayout = computePopoverPosition(anchorRect, measured, filterType);
        setLayout((prev) => {
          if (
            prev &&
            prev.top === measuredLayout.top &&
            prev.left === measuredLayout.left &&
            prev.width === measuredLayout.width &&
            prev.flipped === measuredLayout.flipped &&
            prev.maxHeight === measuredLayout.maxHeight
          ) {
            return prev;
          }
          return measuredLayout;
        });
      });
      return () => cancelAnimationFrame(raf);
    }

    return undefined;
  }, [anchorKey, filterType]);

  if (!anchorRect || !filterType) return null;

  if (filterType === 'osFutura') {
    return (
      <BoardSearchTechnicianOsFuturaPanel
        anchorRect={anchorRect}
        futuraOrders={futuraOrders}
        tempFilters={displayFilters}
        onClose={(nextFilters, applied) => {
          if (applied && nextFilters) onApply(nextFilters);
          onClose();
        }}
      />
    );
  }

  if (!layout) return null;

  const { flipped, ...style } = layout;

  return ReactDOM.createPortal(
    <div
      ref={popoverRef}
      className={[
        'board-search-filters-popover',
        flipped ? 'is-flipped' : '',
        filterType && isSimplePickerFilterType(filterType)
          ? 'board-search-filters-popover--simple'
          : '',
        filterType ? `board-search-filters-popover--${filterType}` : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={style}
      role="dialog"
      aria-modal="false"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <BoardSearchFilterPanelRouter
        key={panelRemountKey}
        filterType={filterType}
        filterScope={filterScope}
        displayFilters={displayFilters}
        onApply={onApply}
        onClose={onClose}
        countsProps={countsProps}
        previsaoWithCounts={previsaoWithCounts}
        retornoCounts={retornoCounts}
      />
    </div>,
    document.body
  );
}
